import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { DPAddress } from '../models/DPAddress';
import { DPFormField } from '../models/DPFormField';
import { DPValidation } from '../models/DPValidation';
import { DPFormFieldType } from '../models/enums/DPFormFieldType';
import { UtilsService } from './utils.service';
import { US_STATES } from '../constants/states';
import { DPValidationType } from '../models/enums/DPValidationType';
import { MultiStepFormService } from './multi-step-form.service';
import { AuthService } from './auth.service';

/**
 * Form validation service.
 *
 * This service exposes form validator generator functions.
 */
@Injectable({
  providedIn: 'root',
})
export class DPValidatorsService {
  constructor(
    private utils: UtilsService,
    private auth: AuthService,
    private multiStepFormService: MultiStepFormService
  ) {}

  // Required validation
  public required(field: DPFormField) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control)) {
        return null;
      }

      return this.checkIsEmpty(field, control.value);
    };
  }

  // Address required validation
  public addressRequired(field: DPFormField) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control)) {
        return null;
      }

      let valid = false;
      try {
        const data: DPAddress = JSON.parse(control.value);

        // If street, city, state and postalCode exist, address is not empty.
        valid = !!(data.street && data.city && data.state && data.postalCode);
      } catch (err) {
        valid = false;
      }

      return valid ? null : { required: true };
    };
  }

  /**
   * Password match validation
   *
   * @param shouldMatch If true, current password field value should match to the other field. If false, they should not match
   * @param fieldToMatch Field to match against
   * @param fieldLabel Other field label
   * @returns {Object} matchField validation object
   */
  public matchField(
    shouldMatch: boolean,
    fieldToMatch: string,
    fieldLabel: string
  ) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const otherControl = control.parent?.get(fieldToMatch);
      const otherValue = otherControl?.value;

      if (
        (shouldMatch && value !== otherValue) ||
        (!shouldMatch && value === otherValue)
      ) {
        return { matchField: { shouldMatch, otherField: fieldLabel } };
      }

      return null;
    };
  }

  // RegEx pattern validation
  public pattern(field: DPFormField, validation: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control) || !control.value) {
        return null;
      }

      const value = (control.value || '').toString();

      const regexp = new RegExp(validation.pattern);

      if (!regexp.test(value)) {
        return {
          pattern: {
            message: validation.message,
          },
        };
      }

      return null;
    };
  }

  /**
   * Minimum length validation
   */
  public minLength(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control) || !control.value) {
        return null;
      }

      const value = (control.value || '').toString();
      const ruleValue = this.utils.getRuleValue(rule);

      if (value.length < +ruleValue) {
        return {
          minLength: {
            message: rule.message,
            length: ruleValue,
          },
        };
      }

      return null;
    };
  }

  /**
   * Maximum length validation
   */
  public maxLength(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control) || !control.value) {
        return null;
      }

      const value = (control.value || '').toString();
      const ruleValue = this.utils.getRuleValue(rule);

      if (value.length > +ruleValue) {
        return {
          maxLength: {
            message: rule.message,
            length: ruleValue,
          },
        };
      }

      return null;
    };
  }

  /**
   * Duration Month Total validation. Only available for multi-value question.
   *
   * This validation is used to ensure that total duration is greater than a certain value in multi-value question
   *
   * @param {Object} rule Validation rule object
   * @param {string} rule.value Minimum total value of sum of duration
   * @param {string} rule.pageQuestionLookupId Duration field ID
   */
  public durationMonthTotal(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control)) {
        return null;
      }

      const value = (control.value || '').toString();

      try {
        const data = JSON.parse(value);

        // Calculate total duration
        const total: number = data.reduce(
          (sum, row) => sum + parseInt(row[rule.pageQuestionLookupId]),
          0
        );

        // Check total duration
        const ruleValue = this.utils.getRuleValue(rule);
        if (total < +ruleValue) {
          throw new Error();
        }
      } catch {
        return {
          durationMonthTotal: {
            message: rule.message,
          },
        };
      }

      return null;
    };
  }

  /**
   * Start/End Date Total validation. Only available for multi-value question.
   *
   * This validation generally specifies duration from now in which the multi-value records can spread.
   * It should consider DateOfBirth validation because if DateOfBirth validation exists, that means
   * all records in multi-value question should be after a certain age(specified in the rule).
   *
   * @param {Object} rule Validation rule object
   * @param {string} rule.value Minimum total value of sum of duration
   * @param {string} rule.pageQuestionLookupId Start/End Date field ID
   */
  public startEndDateTotal(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control)) {
        return null;
      }

      // If rule object is not valid, return null
      const ruleValue = this.utils.getRuleValue(rule);
      if (!rule.pageQuestionLookupId || !ruleValue) {
        return null;
      }

      const data = (control.value || '[]').toString();
      try {
        // Parse mutli-value question value to array of objects.
        const value = JSON.parse(data);

        // Convert start/end date field values to an array.
        // Each array item is an array of 2 strings: [startDate, endDate].
        const dates = value
          .filter((row) => row[rule.pageQuestionLookupId])
          .map((row) => row[rule.pageQuestionLookupId].split(' to '));

        const pivotDate = this.getPivotDate(field);

        // Minimum and maximum dates in dates array
        let minDate: string = dates[0] ? dates[0][0] : pivotDate;
        let maxDate: string = dates[0] ? dates[0][1] : pivotDate;

        // Get minimum and maximum dates in dates array
        for (let i = 1; i < dates.length; i++) {
          if (this.utils.compareDates(minDate, dates[i][0]) > 0) {
            minDate = dates[i][0];
          }
          if (this.utils.compareDates(maxDate, dates[i][1]) < 0) {
            maxDate = dates[i][1];
          }
        }

        // Get difference in month between minDate and maxDate
        const diff = this.utils.getMonthDiff(minDate, maxDate);

        // Get startEnd total value from field
        const ruleValue = this.getStartEndTotalValue(field);

        // If total duration is less than ruleValue, validation fails.
        if (diff < ruleValue) {
          return {
            startEndDateTotal: {
              message: rule.message,
              value: ruleValue,
            },
          };
        }

        // If maxDate is a future date, validation fails.
        if (this.utils.compareDates(maxDate, this.utils.getToday()) > 0) {
          return {
            startEndDateTotal: {
              future: true,
            },
          };
        }

        return null;
      } catch (err) {
        /**
         * Invalid code path.
         * This should never hit.
         */
        return { startEndDateTotal: {} };
      }
    };
  }

  /**
   * If field has startEndDateTotal validation rule, get the rule value considering birthdate field.
   *
   * @param {Object} field Field object
   * @param {Object[]} field.validations Array of validations
   * @param {Object} form FormGroup object
   * @returns {number | null} Rule value
   */
  public getStartEndTotalValue(field: DPFormField): number | null {
    // Find startEndDateTotal validation rule
    const rule3 = field.validations.find(
      (r) => r.validationTypeId === DPValidationType.StartEndDateTotal
    );

    // If rule is not found, return null
    if (!rule3) {
      return null;
    }

    let ruleValue = +this.utils.getRuleValue(rule3);

    // Find DateOfBirth validation
    const rule6 = field.validations.find(
      (r) => r.validationTypeId === DPValidationType.DateOfBirth
    );
    // If DateOfBirth validation is not found, return rule value from StartEndDateTotal
    if (!rule6) {
      return ruleValue;
    }

    // Get value of birthdate field
    let birthDate: string = '';
    if (rule6.getReferredValue) {
      const date = this.utils.toDate(rule6.getReferredValue());
      birthDate = this.utils.getDateString(date);
    } else {
      birthDate = this.multiStepFormService.lookupFieldValue(
        rule6.pageQuestionLookupId
      );
    }

    // If birthdate field has value
    if (birthDate) {
      // Get difference between minimum allowed date and now
      const rule6Value = +this.utils.getRuleValue(rule6);
      const maxDiff =
        this.utils.getMonthDiff(birthDate, this.getPivotDate(field)) -
        rule6Value * 12;

      // The result is minimum of ruleValue and maxDiff
      if (ruleValue > maxDiff) {
        ruleValue = maxDiff;
      }
    }

    return ruleValue;
  }

  /**
   * Get the date to calculate dates from.
   *
   * If EmployeeHireDate validation exists and hireDate is not empty in auth claim, hireDate will be returned.
   * Otherwise, today will be returned.
   */
  public getPivotDate(field: DPFormField) {
    const rule15 = field.validations?.find(
      (v) => v.validationTypeId === DPValidationType.EmploymentHireDate
    );
    if (rule15) {
      const rule15Value = this.utils.getRuleValue(rule15);
      let hireDate = this.utils.toDate(rule15Value);

      if (!hireDate) {
        hireDate = this.auth.HireDate;
      }

      if (hireDate) {
        return this.utils.getDateString(hireDate);
      }
    }

    return this.utils.getToday();
  }

  /**
   * Start/End Date Gap validation.  Only available for multi-value question.
   *
   * @param {Object} field Form field object
   * @param {Object} rule Validation rule
   * @param {string} rule.value Maximum allowed gap between records in multi-value question
   * @param {string} rule.pageQuestionLookupId Start/End date field ID
   * @returns
   */
  public startEndDateGap(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control)) {
        return null;
      }

      const ruleValue = this.utils.getRuleValue(rule);
      if (!rule.pageQuestionLookupId || !ruleValue) {
        return null;
      }

      const data = (control.value || '[]').toString();
      try {
        const value = JSON.parse(data);

        /**
         * Get all start/end dates and create date segmenets array.
         * "dateSegments" is an array of separate date segments in order
         * generated from start/end date field.
         */
        const dateSegments: Date[][] = [];
        for (let i = 0; i < value.length; i++) {
          const strdates = value[i][rule.pageQuestionLookupId].split(' to ');
          const dates = this.utils.normalizeStartEndDate(strdates);

          this.utils.pushDateSegment(dateSegments, dates);
        }

        // Cut dateSegments up to now.
        const pivot = this.utils.toDate(this.getPivotDate(field));
        const newSegments1 = this.utils.getLimitedDateSegmentsByMaxDate(
          dateSegments,
          pivot
        );

        // Get start/end date total value and calculate minimum possible date
        const startEndTotal = this.getStartEndTotalValue(field);
        let minDate: Date = null;
        if (startEndTotal) {
          minDate = new Date(pivot);
          minDate.setMonth(minDate.getMonth() - startEndTotal);
        }

        // Cut dateSegments to minimum possible date.
        const newSegments2 = minDate
          ? this.utils.getLimitedDateSegmentsByMinDate(newSegments1, minDate)
          : newSegments1;

        const allowedGap = +ruleValue;

        // Check if gap between date segments is greater than allowedGap
        for (let i = 0; i < newSegments2.length - 1; i++) {
          const endDate = newSegments2[i][1];
          const startDate = newSegments2[i + 1][0];
          const monthsDiff = this.utils.getMonthDiff1(endDate, startDate);
          if (monthsDiff > allowedGap) {
            return {
              startEndDateGap: true,
            };
          }
        }

        /**
         * Check gap from last end date to now or hire date
         */
        if (newSegments2.length > 0) {
          const lastEndDate = newSegments2[newSegments2.length - 1][1];
          const gapToPivot = this.utils.getMonthDiff1(lastEndDate, pivot);
          if (gapToPivot > allowedGap) {
            return {
              startEndDateGap: true,
            };
          }
        }

        if (minDate) {
          if (newSegments2.length > 0) {
            /**
             * Check if difference between minimum allowed date and
             * first startDate of date segments is greater than allowed gap.
             */
            const startDate = newSegments2[0][0];
            const gapToMin = this.utils.getMonthDiff1(minDate, startDate);

            if (gapToMin > allowedGap) {
              return {
                startEndDateGap: true,
              };
            }
          } else {
            /**
             * If minDate exists without dateSegments, no entries are in valid range.
             * So the validation fails.
             */

            return {
              startEndDateGap: true,
            };
          }
        }

        return null;
      } catch {
        return {
          startEndDateGap: true,
        };
      }
    };
  }

  /**
   * This validation invalidates future dates. Only available for date field.
   *
   * @param {Object} field Field object
   * @param {Object} rule Validation rule object
   * @param {string} rule.message Message from server
   * @returns
   */
  public invalidateFutureDate(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control)) {
        return null;
      }

      const date = (control.value || '').toString();
      try {
        // If the date value is greater than today, validation fails
        if (this.utils.compareDates(date, this.utils.getToday()) > 0) {
          return {
            invalidateFutureDate: {
              message: rule.message,
            },
          };
        }

        return null;
      } catch {}
    };
  }

  /**
   * This validation invalidates past dates. Only available for date field.
   *
   * @param {Object} field Field object
   * @param {Object} rule Validation rule object
   * @param {string} rule.message Message from server
   * @returns
   */
  public invalidatePastDate(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control)) {
        return null;
      }

      const date = (control.value || '').toString();
      try {
        // If the date value is less than today, validation fails
        if (this.utils.compareDates(date, this.utils.getToday()) < 0) {
          return {
            invalidatePastDate: {
              message: rule.message,
            },
          };
        }
      } catch {}
      return null;
    };
  }

  /**
   * Current residence validation. Only available for multi-value question.
   *
   * This validation ensures that only one current residence entry exists
   * in multi-value question.
   *
   * @param {Object} field Field object
   * @param {Object} rule Validation rule object
   * @param {string} rule.pageQuestionLookupId Current residence field ID. Yes/No field.
   * @param {string} rule.message Message from server
   * @returns
   */
  public currentResidence(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control) || !control.value) {
        return null;
      }

      const data = (control.value || '').toString();
      try {
        const value = JSON.parse(data);

        // Get all current residence entries
        const currentEntries = value.filter((row) =>
          row[rule.pageQuestionLookupId]?.includes('Yes')
        );

        if (currentEntries.length !== 1) {
          return {
            currentResidence: {
              message: rule.message,
            },
          };
        }
      } catch {}
      return null;
    };
  }

  /**
   * Current residence subfield validation. Available in multi-value entry form popup.
   *
   * This validation is used to prevent submitting the subform when there is already
   * another current residence record and you are selecting "yes" on that field again.
   */
  public currentResidenceSubfield(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control)) {
        return null;
      }

      if (control.value.includes('Yes')) {
        return {
          currentResidenceSubfield: {
            message: rule.message,
          },
        };
      }

      return null;
    };
  }

  /**
   * Driver license validation.
   *
   * This validation looks at the state field and validate the driver license value
   * using the state's DL format.
   *
   * @param {Object} field Field object
   * @param {Object} rule Validation rule object
   * @param {string} rule.pageQuestionLookupId State field ID
   * @returns
   */
  public driverLicense(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // If the field is a hidden subfield, return null
      if (!this.isFieldVisible(field, control)) {
        return null;
      }

      if (!rule.pageQuestionLookupId || !control.parent) {
        return null;
      }

      // Get state control
      const stateControl = control.parent.get(`${rule.pageQuestionLookupId}`);

      if (!stateControl) {
        return null;
      }

      try {
        // Get state value
        const state = Object.values(JSON.parse(stateControl.value))[0];
        const dlNumber = control.value;

        // Find stateObject with DL format
        const stateObject = US_STATES.find((s) => s.abbreviation === state);
        if (stateObject?.format) {
          const regex = new RegExp(stateObject.format);
          if (!regex.test(dlNumber)) {
            return {
              driverLicense: {
                message: rule.message,
                state: stateObject.name,
              },
            };
          }
        }
      } catch {}
      return null;
    };
  }

  /**
   * Max Date Year Limit validation. Available for Date field
   *
   * This validation specifies maximum years allowed from now.
   *
   * @param {Object} field Field object
   * @param {Object} rule Validation rule object
   * @param {string} rule.value Number of years that is allowed from now
   * @returns
   */
  public dateYearLimit(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      const ruleValue = +this.utils.getRuleValue(rule);
      if (
        !ruleValue ||
        isNaN(ruleValue) ||
        !this.utils.isValidDate(control.value) ||
        !this.isFieldVisible(field, control)
      ) {
        return null;
      }

      const year = this.utils.toDate(control.value)?.getFullYear();
      const thisYear = new Date().getFullYear();

      if (year > thisYear + ruleValue || year < thisYear - ruleValue) {
        return {
          dateYearLimit: {
            message: rule.message,
          },
        };
      }

      return null;
    };
  }

  /**
   * Min Date Year Limit validation. Available for Date field
   *
   * This validation specifies maximum years allowed from now.
   *
   * @param {Object} field Field object
   * @param {Object} rule Validation rule object
   * @param {string} rule.value Number of years that is allowed from now
   * @returns
   */
  public minDateYearLimit(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      const ruleValue = +this.utils.getRuleValue(rule);
      if (
        !ruleValue ||
        isNaN(ruleValue) ||
        !this.utils.isValidDate(control.value) ||
        !this.isFieldVisible(field, control)
      ) {
        return null;
      }

      const year = this.utils.toDate(control.value)?.getFullYear();
      const thisYear = new Date().getFullYear();

      if (year > thisYear - ruleValue) {
        return {
          dateYearLimit: {
            message: rule.message,
          },
        };
      }

      return null;
    };
  }

  /**
   * Date of Birth Plus validation. Available for multi-value question.
   *
   * This validation looks at the birthdate field and calculate minimum allowed date of entries.
   * All entries in multi-value question should be later than the minimum date.
   *
   * @param {Object} field Field object
   * @param {Object} rule Validation rule object
   * @param {string} rule.pageQuestionLookupId Date of birth field ID
   * @param {string} rule.value Years from date of birth. This is used to calculate minimum date.
   *
   * @returns
   */
  public dateOfBirthPlus(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.isFieldVisible(field, control)) {
        return null;
      }

      const ruleValue = this.utils.getRuleValue(rule);
      if (!ruleValue) {
        return;
      }

      const value = +ruleValue;
      const birthDate = this.multiStepFormService.lookupFieldValue(
        rule.pageQuestionLookupId
      );
      if (birthDate) {
        // Difference between birthdate and entered date
        const diff = this.utils.getMonthDiff(birthDate, control.value);

        if (diff < value * 12) {
          return {
            dateOfBirthPlus: {
              message: rule.message,
              value,
            },
          };
        }
      }

      return null;
    };
  }

  /**
   * Yes/no subfield required validation. Available for subfield of yes/no question.
   *
   * This validation ensures that all subfields of yes/no question are required.
   * If they are empty, it will return validation error.
   *
   * @param {Object} field Field object
   * @returns
   */
  public yesnoSubFieldRequired(field: DPFormField) {
    return (control: AbstractControl): ValidationErrors | null => {
      let value: Record<string, any> = {};
      try {
        value = JSON.parse(control.value);
      } catch {}

      if (value[field.name] === 'Yes') {
        const subFields = field.subFields || [];
        for (let i = 0; i < subFields.length; i++) {
          if (
            subFields[i].name &&
            this.checkIsEmpty(subFields[i], value[subFields[i].name])
          ) {
            return { yesnoSubFieldRequired: true };
          }
        }
      }

      return null;
    };
  }

  /**
   * This is a custom validation for employers field in Employment History step
   * in application edit flow
   * @param field Form field
   */
  public noPendingConflicts() {
    return (control: AbstractControl): ValidationErrors | null => {
      const data = (control.value || '[]').toString();

      try {
        const value = JSON.parse(data);

        for (let i = 0; i < value.length; i++) {
          if (
            value[i].conflicts &&
            !!value[i].employerResponse &&
            !value[i].employerResponse.userResponse
          ) {
            return {
              employerResponseConflicts: true,
            };
          }
        }

        return null;
      } catch {
        return null;
      }
    };
  }

  /**
   * Check if this field is a subfield and hidden.
   *
   * If the field is a hidden subfield, it should skip validation.
   *
   * The visibility of a subfield is determined by field.subQuestionTriggerOptionId.
   * If the option is selected, subfield is visible.
   */
  public isFieldVisible(field: DPFormField, control: AbstractControl) {
    // If not a subfield, show the field
    if (!field.parentField) {
      return true;
    }

    // If subfield doesn't have parent control, show the field
    if (!control.parent) {
      return false;
    }

    // If subQuestionTriggerOptionId is not set, show the field
    if (!field.subQuestionTriggerOptionId && !field.subQuestionTriggerOption) {
      return true;
    }

    const formValue = control.parent.value;
    try {
      if (
        field.subQuestionTriggerOptionId !== undefined &&
        field.subQuestionTriggerOptionId !== null
      ) {
        const parentValue = JSON.parse(formValue[field.parentField] || '{}');
        const triggerOptionIds = (field.subQuestionTriggerOptionId || '')
          .toString()
          .split(',');
        return triggerOptionIds.some(
          (optionId) => parentValue[optionId] !== undefined
        );
      }

      return formValue[field.parentField] === field.subQuestionTriggerOption;
    } catch {
      return false;
    }
  }

  /**
   * Check if value is empty for given form field type.
   *
   * @param {Object} field Field object
   * @param {any} value Value to check
   * @returns
   */
  private checkIsEmpty(field: DPFormField, value: any) {
    if (field.type === DPFormFieldType.Checkbox && value === '{}') {
      return { required: true };
    } else if (field.type === DPFormFieldType.Multivalue) {
      try {
        const data = JSON.parse(value);

        if (!data?.length) {
          return { required: true };
        }
      } catch {
        return { required: true };
      }
    } else if (field.type === DPFormFieldType.StartEndDate) {
      const [start, end] = (value || '').split(' to ');
      const startEmpty = !start || start === 'null';
      const endEmpty = !end || end === 'null';

      if (startEmpty && endEmpty) {
        return { custom: 'Please enter start and end date' };
      } else if (startEmpty) {
        return { custom: 'Please enter start date' };
      } else if (endEmpty) {
        return { custom: 'Please enter end date' };
      } else if (this.utils.compareDates(start, end) > 0) {
        return { custom: 'Start date must be before end date' };
      }
    }

    return value ? null : { required: true };
  }

  /**
   * Minimum start date validation. Available for StartEndDate field
   *
   * @param {Object} field Field object
   * @param {Object} rule Validation rule object
   * @param {string} rule.value Number of years that is allowed from now
   * @returns
   */
  public minStartDate(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // Skip validation if field is hidden or empty
      if (!this.isFieldVisible(field, control) || !control.value) {
        return null;
      }

      const ruleValue = +this.utils.getRuleValue(rule);
      if (!ruleValue || isNaN(ruleValue)) {
        return null;
      }

      const [startDate] = (control.value || '').split(' to ');
      if (
        !startDate ||
        startDate === 'null' ||
        !this.utils.isValidDate(startDate)
      ) {
        return null;
      }

      const minAllowedDate = new Date();
      minAllowedDate.setFullYear(minAllowedDate.getFullYear() - ruleValue);

      const inputDate = this.utils.toDate(startDate);
      if (inputDate && inputDate < minAllowedDate) {
        return {
          minStartDate: {
            message: rule.message,
          },
        };
      }

      return null;
    };
  }

  /**
   * Maximum end date validation. Available for StartEndDate field
   *
   * @param {Object} field Field object
   * @param {Object} rule Validation rule object
   * @param {string} rule.value Number of years that is allowed from now
   * @returns
   */
  public maxEndDate(field: DPFormField, rule: DPValidation) {
    return (control: AbstractControl): ValidationErrors | null => {
      // Skip validation if field is hidden or empty
      if (!this.isFieldVisible(field, control) || !control.value) {
        return null;
      }

      const ruleValue = +this.utils.getRuleValue(rule);
      if (!ruleValue || isNaN(ruleValue)) {
        return null;
      }

      const [, endDate] = (control.value || '').split(' to ');
      if (!endDate || endDate === 'null' || !this.utils.isValidDate(endDate)) {
        return null;
      }

      const maxAllowedDate = new Date();
      maxAllowedDate.setFullYear(maxAllowedDate.getFullYear() + ruleValue);

      const inputDate = this.utils.toDate(endDate);
      if (inputDate && inputDate > maxAllowedDate) {
        return {
          maxEndDate: {
            message: rule.message,
          },
        };
      }

      return null;
    };
  }
}
